import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  contentFeeApi,
  type RegisterBillingKeyRequest,
  type PurchaseContentFeeRequest,
  type RefundContentFeeRequest,
} from '@/api/contentFee'
import { lcEnrollmentApi, type LcCourseChangeRequest } from '@/api/lc'
import { getApiErrorMessage } from '@/utils/apiError'

export const contentFeeKeys = {
  all: ['contentFee'] as const,
  quote: (userId: number, formalLicenseId?: number) =>
    ['contentFee', 'quote', userId, formalLicenseId ?? 'progress'] as const,
  current: (userId: number) => ['contentFee', 'current', userId] as const,
  orders: (userId?: number) => ['contentFee', 'orders', userId ?? 'all'] as const,
  billingKeys: () => ['contentFee', 'billingKeys'] as const,
}

/**
 * 환불·과정 변경·결제 성공이 공유하는 캐시 무효화 목록
 * (spec: content-fee-refund-course-change-spec.md §캐시 무효화).
 */
export function invalidateEnrollmentAndFeeCaches(qc: QueryClient, userId: number) {
  qc.invalidateQueries({ queryKey: ['contentFee', 'quote', userId] })
  qc.invalidateQueries({ queryKey: contentFeeKeys.current(userId) })
  qc.invalidateQueries({ queryKey: ['contentFee', 'orders'] })
  qc.invalidateQueries({ queryKey: ['lcFreeTrialHistory', userId] })
  qc.invalidateQueries({ queryKey: ['lcFreeTrials'] })
  qc.invalidateQueries({ queryKey: ['lcEnrollments'] })
  qc.invalidateQueries({ queryKey: ['lcRoster'] })
  qc.invalidateQueries({ queryKey: ['lcRosterSearch'] })
  qc.invalidateQueries({ queryKey: ['coaching', 'classes'] })
  qc.invalidateQueries({ queryKey: ['textbookRequests', 'student', userId] })
  qc.invalidateQueries({ queryKey: ['textbookRequests', 'alerts'] })
}

export function useContentFeeQuote(userId: number, enabled: boolean, formalLicenseId?: number) {
  return useQuery({
    queryKey: contentFeeKeys.quote(userId, formalLicenseId),
    queryFn: async () => (
      await (formalLicenseId == null
        ? contentFeeApi.getQuote(userId)
        : contentFeeApi.getQuote(userId, formalLicenseId))
    ).data.data,
    enabled: enabled && Number.isFinite(userId) && userId > 0,
    staleTime: 30_000,
  })
}

export function useCurrentEntitlement(userId: number) {
  return useQuery({
    queryKey: contentFeeKeys.current(userId),
    queryFn: async () => (await contentFeeApi.getCurrent(userId)).data.data,
    enabled: Number.isFinite(userId) && userId > 0,
    retry: false,
  })
}

export function useContentFeeOrders(userId?: number) {
  return useQuery({
    queryKey: contentFeeKeys.orders(userId),
    queryFn: async () => (await contentFeeApi.getOrders(userId)).data.data ?? [],
  })
}

export function useBillingKeys(enabled = true) {
  return useQuery({
    queryKey: contentFeeKeys.billingKeys(),
    queryFn: async () => (await contentFeeApi.getBillingKeys()).data.data ?? [],
    enabled,
    staleTime: 30_000,
  })
}

export function useRegisterBillingKey() {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: (body: RegisterBillingKeyRequest) => contentFeeApi.registerBillingKey(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentFeeKeys.billingKeys() })
      toast.success(t('admin:students.detail.contentFee.billingKeys.registration.success'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}

export function useDeleteBillingKey() {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: (id: number) => contentFeeApi.deleteBillingKey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentFeeKeys.billingKeys() })
      toast.success(t('admin:students.detail.contentFee.billingKeys.deleteSuccess'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}

export function usePurchaseContentFee(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: (body: PurchaseContentFeeRequest) => contentFeeApi.purchase(userId, body),
    onSuccess: (_response, variables) => {
      invalidateEnrollmentAndFeeCaches(qc, userId)
      qc.invalidateQueries({ queryKey: contentFeeKeys.billingKeys() })
      toast.success(
        t(
          variables.billingKeyId === null
            ? 'admin:students.detail.contentFee.toast.freePurchaseSuccess'
            : 'admin:students.detail.contentFee.toast.purchaseSuccess',
        ),
      )
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}

export function useRefundContentFee(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: (body: RefundContentFeeRequest) => contentFeeApi.refund(userId, body),
    onSuccess: () => {
      invalidateEnrollmentAndFeeCaches(qc, userId)
      toast.success(t('admin:students.detail.contentFee.toast.refundSuccess'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}

/** 과정 변경(A→B). 성공 시 수강·수강권·교재 캐시를 모두 무효화한다. */
export function useChangeCourse(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: ({ sourceEnrollmentId, body }: { sourceEnrollmentId: number; body: LcCourseChangeRequest }) =>
      lcEnrollmentApi.changeCourse(sourceEnrollmentId, body),
    onSuccess: () => {
      invalidateEnrollmentAndFeeCaches(qc, userId)
      qc.invalidateQueries({ queryKey: ['lcTimeslots'] })
      qc.invalidateQueries({ queryKey: ['lc', 'learnerStatuses'] })
      toast.success(t('admin:roster.action.toast.changeCourseSuccess'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}
